"use client";

import { memo, forwardRef } from "react";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";

import { Root, BadgeCount } from "./styled";
import { SIZE_TOKENS, DEFAULT_SIZE, DEFAULT_TONE, DEFAULT_VARIANT } from "./tokens";

import type { IconButtonSize, IconButtonProps } from "./types";

const BADGE_MAX = 99;

/** Spinner que ocupa o mesmo espaço do ícone — evita layout shift no loading. */
function IconButtonSpinner({ size }: { size: IconButtonSize }) {
  const t = SIZE_TOKENS[size];
  return (
    <Box sx={{ display: "grid", placeItems: "center", width: t.iconFontSize, height: t.iconFontSize }}>
      {/* Spinner é decorativo: o nome acessível vem do próprio IconButton
         (aria-label). Sem aria-hidden o MUI expõe role="progressbar" sem
         nome e o axe acusa "aria-progressbar-name". */}
      <CircularProgress
        size={t.spinner}
        thickness={5}
        color="inherit"
        aria-hidden="true"
      />
    </Box>
  );
}

// ============================================================================
// Componente
// ============================================================================

/**
 * **IconButton** — botão de ação que mostra apenas um ícone.
 *
 * Diferente de `<Button>`, este é otimizado para ações onde o ícone
 * comunica sozinho (fechar, voltar, mais opções, deletar). O `ariaLabel`
 * é **obrigatório em compile-time** — botões-só-ícone sem label são uma
 * armadilha clássica de acessibilidade.
 *
 * **Variantes**
 *  - `ghost` (default): sem fundo, hover sutil — toolbars e listas.
 *  - `tinted`: fundo com alpha — destacado sem virar CTA.
 *  - `solid`: fundo sólido — alta atenção, raro.
 *
 * **Acessibilidade**
 *  - `ariaLabel` exigido pela tipagem.
 *  - `:focus-visible` cria anel só para teclado.
 *  - Estado de loading desabilita interação e mantém o tamanho (sem shift).
 *
 * @example Fechar diálogo
 * ```tsx
 * <IconButton ariaLabel="Fechar" onClick={onClose}>
 *   <Icon name="solar:close-circle-bold" />
 * </IconButton>
 * ```
 *
 * @example Deletar (danger + tinted)
 * ```tsx
 * <IconButton ariaLabel="Excluir" tone="danger" variant="tinted" onClick={remove}>
 *   <Icon name="solar:trash-bin-trash-bold" />
 * </IconButton>
 * ```
 */
function IconButtonImpl(
  {
    children,
    ariaLabel,
    onClick,
    variant = DEFAULT_VARIANT,
    tone = DEFAULT_TONE,
    size = DEFAULT_SIZE,
    loading = false,
    disabled = false,
    selected,
    badge,
    type = "button",
    sx,
    className,
    ...rest
  }: IconButtonProps,
  ref: React.Ref<HTMLButtonElement>
) {
  return (
    <Root
      ref={ref}
      $variant={variant}
      $tone={tone}
      $size={size}
      $selected={selected ?? false}
      onClick={onClick}
      aria-label={ariaLabel}
      aria-busy={loading || undefined}
      // Só vira toggle (expõe estado pressionado) quando `selected` é definido.
      aria-pressed={selected}
      disabled={disabled || loading}
      type={type}
      disableRipple
      className={className}
      sx={sx}
      {...rest}
    >
      {loading ? <IconButtonSpinner size={size} /> : children}
      {badge != null && badge > 0 ? (
        <BadgeCount aria-hidden="true" data-testid="icon-button-badge">
          {badge > BADGE_MAX ? `${BADGE_MAX}+` : badge}
        </BadgeCount>
      ) : null}
    </Root>
  );
}

export const IconButton = memo(forwardRef(IconButtonImpl));
IconButton.displayName = "IconButton";
